import React, { useCallback } from "react";

import CapturedOnWheel from "../Capture/components/CapturedOnWheel";
import { useCaptureSegmentDrop } from "./CaptureSegmentDropContext";

/**
 * BuilderCapturedOnWheel — the Captured drawer AS MOUNTED ON THE BUILDER.
 *
 * `CapturedOnWheel` is shared with ViewWheel, where there is no craft canvas and
 * so no Segment to insert into. Rather than teach the shared component about the
 * builder, this thin builder-side wrapper supplies the two props that only exist
 * here: the wheel's Segment list (for the "Add to Segment" picker) and the
 * insert function.
 *
 * `onInsertIntoSegment` is only passed when the feature is enabled. The drawer
 * derives `canAddToSegment` from that prop being present, so a disabled build
 * renders the drawer exactly as it does today — no dead "Add to Segment" control
 * that does nothing when pressed.
 *
 * ISP-9999: mounted inside the builder's own side-pane slot now (same place
 * every other rail pane renders), so it always passes `embedded` — no fixed
 * overlay, no backdrop, the slot's own width/position/animation owns it.
 */
const BuilderCapturedOnWheel = ({ isOpen, onClose, wheelId, wheelName }) => {
  const { isEnabled, wheelSections, place, createSegmentAndPlace } =
    useCaptureSegmentDrop();

  const handleInsert = useCallback(
    (item, segmentId) => place(item, segmentId),
    [place],
  );

  // ISP-9999 add-to-segment-autocreate — a wheel with zero Segments has no
  // existing Segment to pass `handleInsert`, so the drawer gets a distinct
  // entry point for that one case rather than being handed a fake segmentId.
  const handleAutoCreate = useCallback(
    (item) => createSegmentAndPlace(item),
    [createSegmentAndPlace],
  );

  return (
    <CapturedOnWheel
      isOpen={isOpen}
      onClose={onClose}
      wheelId={wheelId}
      wheelName={wheelName}
      embedded
      wheelSections={isEnabled ? wheelSections : []}
      onInsertIntoSegment={isEnabled ? handleInsert : null}
      onAutoCreateSegment={isEnabled ? handleAutoCreate : null}
    />
  );
};

export default BuilderCapturedOnWheel;
